import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import AdminLayout from '../../components/AdminLayout';
import SectionOverview from '../../components/campaign/SectionOverview';
import { Images, FileText, ClipboardList, CircleQuestionMark } from 'lucide-react';

const DEFAULT_FONT = 'URW Geometric';
const ASSET_BASE_URL = process.env.REACT_APP_ASSET_URL || process.env.REACT_APP_HOSTNAME || '';

const normalizeDefaultFont = (value) => {
  if (!value) return DEFAULT_FONT;
  const v = value.trim().toLowerCase();
  if (v === 'inter' || v === 'quicksand') return DEFAULT_FONT;
  return value;
};

function getAssetBaseUrl(apiClient) {
  if (ASSET_BASE_URL) return ASSET_BASE_URL;
  if (apiClient?.defaults?.baseURL) return apiClient.defaults.baseURL.replace(/\/api\/?$/, '');
  return '';
}

function ColorField({ label, fieldKey, value, onChange }) {
  const handleTextChange = (e) => {
    const val = e.target.value;
    onChange(fieldKey, val);
  };

  const handlePickerChange = (e) => {
    onChange(fieldKey, e.target.value);
  };

  return (
    <div>
      <label className="inline-block px-4 py-2 bg-teal-700 text-white rounded-lg text-sm font-medium mb-3">
        {label}
      </label>
      <div className="flex items-center gap-3">
        <input
          type="color"
          value={/^#[0-9A-Fa-f]{6}$/.test(value) ? value : '#000000'}
          onChange={handlePickerChange}
          className="w-14 h-14 rounded-xl border border-gray-300 cursor-pointer p-1 flex-shrink-0"
        />
        <div className="flex-1">
          <input
            type="text"
            value={value}
            onChange={handleTextChange}
            pattern="^#[0-9A-Fa-f]{6}$"
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm font-mono"
            placeholder="#000000"
          />
          <p className="text-xs text-gray-400 mt-1 pl-1">Hex color e.g. #1a2b3c</p>
        </div>
      </div>
      {value && !/^#[0-9A-Fa-f]{6}$/.test(value) && (
        <p className="text-xs text-red-500 mt-1">Must be a valid hex color (e.g. #1a2b3c)</p>
      )}
    </div>
  );
}

function TemplateEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [activeLayoutTab, setActiveLayoutTab] = useState('landing');
  const [uploadingHeaderFont, setUploadingHeaderFont] = useState(false);
  const [uploadingParagraphFont, setUploadingParagraphFont] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [sections, setSections] = useState([]);
  const [winnerSections, setWinnerSections] = useState([]);
  const [comingSoonSections, setComingSoonSections] = useState([]);
  const [showSectionTypeModal, setShowSectionTypeModal] = useState(false);
  const [selectedSectionType, setSelectedSectionType] = useState('content');
  const [currentPageType, setCurrentPageType] = useState('landing_page');
  const [formData, setFormData] = useState({
    name: '',
    primary_color: '#000000',
    secondary_color: '#000000',
    accent_color: '#000000',
    logo_url: '',
    header_font: DEFAULT_FONT,
    paragraph_font: DEFAULT_FONT,
    header_font_name: '',
    header_font_file_url: '',
    paragraph_font_name: '',
    paragraph_font_file_url: '',
  });

  const fetchTemplate = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(`/admin/templates/${id}`);
      const t = response.data.template;
      const headerFont = normalizeDefaultFont(t.header_font);
      const paragraphFont = normalizeDefaultFont(t.paragraph_font);
      const headerFontName = t.header_font_name != null ? normalizeDefaultFont(t.header_font_name) : headerFont;
      const paragraphFontName = t.paragraph_font_name != null ? normalizeDefaultFont(t.paragraph_font_name) : paragraphFont;
      setFormData({
        name: t.name,
        primary_color: t.primary_color,
        secondary_color: t.secondary_color,
        accent_color: t.accent_color,
        logo_url: t.logo_url || '',
        header_font: headerFont,
        paragraph_font: paragraphFont,
        header_font_name: headerFontName,
        header_font_file_url: t.header_font_file_url || '',
        paragraph_font_name: paragraphFontName,
        paragraph_font_file_url: t.paragraph_font_file_url || '',
      });

      const sectionRequests = await Promise.allSettled([
        api.get(`/admin/templates/${id}/sections`, { params: { page_type: 'landing_page' } }),
        api.get(`/admin/templates/${id}/sections`, { params: { page_type: 'winner_page' } }),
        api.get(`/admin/templates/${id}/sections`, { params: { page_type: 'coming_soon_page' } }),
      ]);

      setSections(sectionRequests[0].status === 'fulfilled' ? (sectionRequests[0].value.data.sections || []) : []);
      setWinnerSections(sectionRequests[1].status === 'fulfilled' ? (sectionRequests[1].value.data.sections || []) : []);
      setComingSoonSections(sectionRequests[2].status === 'fulfilled' ? (sectionRequests[2].value.data.sections || []) : []);
    } catch (err) {
      console.error('Failed to fetch template:', err);
      alert('Failed to load template');
      navigate('/admin/templates');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchTemplate();
  }, [fetchTemplate]);

  const getSectionsByType = (pageType) => {
    switch (pageType) {
      case 'landing_page':
        return sections;
      case 'winner_page':
        return winnerSections;
      case 'coming_soon_page':
        return comingSoonSections;
      default:
        return sections;
    }
  };

  const updateSectionsByType = (pageType, updater) => {
    switch (pageType) {
      case 'landing_page':
        setSections(prev => updater(prev));
        break;
      case 'winner_page':
        setWinnerSections(prev => updater(prev));
        break;
      case 'coming_soon_page':
        setComingSoonSections(prev => updater(prev));
        break;
      default:
        setSections(prev => updater(prev));
        break;
    }
  };

  const handleAddSection = (pageType = 'landing_page') => {
    setCurrentPageType(pageType);
    setSelectedSectionType('content');
    setShowSectionTypeModal(true);
  };

  const handleConfirmAddSection = async () => {
    if (!selectedSectionType) return;
    setShowSectionTypeModal(false);

    try {
      const targetSections = getSectionsByType(currentPageType);
      const response = await api.post(`/admin/templates/${id}/sections`, {
        type: selectedSectionType,
        page_type: currentPageType,
        sort_order: targetSections.length
      });

      updateSectionsByType(currentPageType, prev => [...prev, response.data.section]);
    } catch (error) {
      console.error('Failed to add section:', error);
      alert(error.response?.data?.error || 'Failed to add section');
    }
  };

  const handleDeleteSection = async (sectionId, pageType) => {
    try {
      await api.delete(`/admin/templates/${id}/sections/${sectionId}`);
      updateSectionsByType(pageType, prev => prev.filter(section => section.id !== sectionId));
    } catch (error) {
      console.error('Failed to delete section:', error);
      alert(error.response?.data?.error || 'Failed to delete section');
    }
  };

  const handleDuplicateSection = async (section, pageType) => {
    try {
      const targetSections = getSectionsByType(pageType);
      const duplicatePayload = {
        type: section.type,
        page_type: pageType,
        template_codename: section.template_codename || null,
        data: {
          template_id: section.template_id || null,
          background_image_url: section.background_image_url || null,
          full_width_background: !!section.full_width_background,
          about_header: section.about_header ? `${section.about_header} (Copy)` : null,
          about_description: section.about_description || null,
          youtube_video_url: section.youtube_video_url || null,
          about_with_form: !!section.about_with_form,
          faqs_header: section.faqs_header ? `${section.faqs_header} (Copy)` : null,
          faqs_description: section.faqs_description || null,
          winner_display_type: section.winner_display_type || 'none'
        },
        sort_order: targetSections.length
      };

      const response = await api.post(`/admin/templates/${id}/sections`, duplicatePayload);
      updateSectionsByType(pageType, prev => [...prev, response.data.section]);
    } catch (error) {
      console.error('Failed to duplicate section:', error);
      alert(error.response?.data?.error || 'Failed to duplicate section');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleColorChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleThemeUpdate = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/svg+xml', 'image/webp'];
    if (!validImageTypes.includes(file.type)) {
      alert('Please upload a valid image file (JPG, PNG, GIF, SVG, WEBP)');
      return;
    }

    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('type', 'logo');

      const response = await api.post(`/admin/templates/${id}/theme/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setFormData(prev => ({ ...prev, logo_url: response.data.url }));
    } catch (err) {
      console.error('Failed to upload logo:', err);
      alert(err.response?.data?.error || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
      e.target.value = '';
    }
  };

  const handleRemoveLogo = async () => {
    try {
      await api.delete(`/admin/templates/${id}/theme/logo`);
      setFormData(prev => ({ ...prev, logo_url: '' }));
    } catch (err) {
      console.error('Failed to remove logo:', err);
      alert(err.response?.data?.error || 'Failed to remove logo');
    }
  };

  const handleHeaderFontUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const ext = file.name.toLowerCase().split('.').pop();
    if (!['ttf', 'otf'].includes(ext)) {
      alert('Please upload a TTF or OTF font file');
      return;
    }
    setUploadingHeaderFont(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('type', 'header');
      const response = await api.post(`/admin/templates/${id}/fonts/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const fontName = formData.header_font_name || file.name.replace(/\.[^/.]+$/, '');
      setFormData(prev => ({
        ...prev,
        header_font_file_url: response.data.url,
        header_font_name: prev.header_font_name || fontName,
      }));
    } catch (err) {
      console.error('Failed to upload header font:', err);
      alert(err.response?.data?.error || 'Failed to upload font file.');
    } finally {
      setUploadingHeaderFont(false);
      e.target.value = '';
    }
  };

  const handleParagraphFontUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const ext = file.name.toLowerCase().split('.').pop();
    if (!['ttf', 'otf'].includes(ext)) {
      alert('Please upload a TTF or OTF font file');
      return;
    }
    setUploadingParagraphFont(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('type', 'paragraph');
      const response = await api.post(`/admin/templates/${id}/fonts/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const fontName = formData.paragraph_font_name || file.name.replace(/\.[^/.]+$/, '');
      setFormData(prev => ({
        ...prev,
        paragraph_font_file_url: response.data.url,
        paragraph_font_name: prev.paragraph_font_name || fontName,
      }));
    } catch (err) {
      console.error('Failed to upload paragraph font:', err);
      alert(err.response?.data?.error || 'Failed to upload font file.');
    } finally {
      setUploadingParagraphFont(false);
      e.target.value = '';
    }
  };

  const handleRemoveFont = (type) => {
    if (type === 'header') {
      setFormData(prev => ({ ...prev, header_font_file_url: '', header_font_name: prev.header_font_name || prev.header_font || '' }));
    } else {
      setFormData(prev => ({ ...prev, paragraph_font_file_url: '', paragraph_font_name: prev.paragraph_font_name || prev.paragraph_font || '' }));
    }
  };

  const isValid = () => {
    const hexRe = /^#[0-9A-Fa-f]{6}$/;
    return (
      formData.name.trim() &&
      hexRe.test(formData.primary_color) &&
      hexRe.test(formData.secondary_color) &&
      hexRe.test(formData.accent_color) &&
      (formData.header_font || formData.header_font_name) &&
      (formData.paragraph_font || formData.paragraph_font_name)
    );
  };

  const handleSave = async () => {
    if (!isValid()) return;
    setSaving(true);
    try {
      await api.put(`/admin/templates/${id}`, {
        ...formData,
        header_font: normalizeDefaultFont(formData.header_font),
        paragraph_font: normalizeDefaultFont(formData.paragraph_font),
        logo_url: formData.logo_url || null,
        header_font_name: formData.header_font_name ? normalizeDefaultFont(formData.header_font_name) : null,
        header_font_file_url: formData.header_font_file_url || null,
        paragraph_font_name: formData.paragraph_font_name ? normalizeDefaultFont(formData.paragraph_font_name) : null,
        paragraph_font_file_url: formData.paragraph_font_file_url || null,
      });
      navigate('/admin/templates');
    } catch (err) {
      console.error('Failed to save template:', err);
      alert(err.response?.data?.error || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout activeSection="TEMPLATES">
        <div className="flex items-center justify-center h-64">
          <div className="text-white">Loading...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout activeSection="TEMPLATES">
      <div className="flex items-center mb-6">
        <button
          onClick={() => navigate('/admin/templates')}
          className="text-white hover:text-gray-200 mr-4"
          aria-label="Back to templates"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
        </button>
        <h1 className="text-4xl font-bold text-white">EDIT TEMPLATE</h1>
      </div>

      <div className="bg-white rounded-3xl shadow-2xl p-8 space-y-8">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('general')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'general'
                  ? 'border-teal-500 text-teal-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              General
            </button>
            <button
              onClick={() => setActiveTab('layout')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'layout'
                  ? 'border-teal-500 text-teal-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Layout
            </button>
          </nav>
        </div>

        {activeTab === 'general' ? (
          <div className="space-y-10">
            <div>
              <h2 className="text-lg font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-200">
                Template Name
              </h2>
              <div className="max-w-md">
                <label className="inline-block px-4 py-2 bg-teal-700 text-white rounded-lg text-sm font-medium mb-3">
                  Template Name
                </label>
                <input
                  type="text"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  placeholder="Enter template name"
                />
              </div>
            </div>

            <div>
              <h2 className="text-lg font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-200">
                Colors
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <ColorField
                  label="Primary Color"
                  fieldKey="primary_color"
                  value={formData.primary_color}
                  onChange={handleColorChange}
                />
                <ColorField
                  label="Secondary Color"
                  fieldKey="secondary_color"
                  value={formData.secondary_color}
                  onChange={handleColorChange}
                />
                <ColorField
                  label="Accent Color"
                  fieldKey="accent_color"
                  value={formData.accent_color}
                  onChange={handleColorChange}
                />
              </div>

              <div className="mt-6 flex gap-4 items-center">
                <span className="text-sm text-gray-500 font-medium">Preview:</span>
                <div className="flex gap-2">
                  {[
                    { color: formData.primary_color, label: 'Primary' },
                    { color: formData.secondary_color, label: 'Secondary' },
                    { color: formData.accent_color, label: 'Accent' },
                  ].map(({ color, label }) => (
                    <div key={label} className="flex flex-col items-center gap-1">
                      <div
                        className="w-12 h-12 rounded-xl border border-gray-200 shadow-sm"
                        style={{ backgroundColor: /^#[0-9A-Fa-f]{6}$/.test(color) ? color : '#e5e7eb' }}
                      />
                      <span className="text-xs text-gray-400">{label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-lg font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-200">
                Logo
              </h2>
              {formData.logo_url ? (
                <img
                  src={`${getAssetBaseUrl(api)}${formData.logo_url}`}
                  alt="Template logo"
                  className="h-20 object-contain mb-4"
                />
              ) : null}
              <div className="flex items-center space-x-4">
                <label className="cursor-pointer px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300">
                  <span>{uploadingLogo ? 'Uploading...' : 'Upload Logo'}</span>
                  <input
                    type="file"
                    className="sr-only"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    disabled={uploadingLogo}
                  />
                </label>
                {formData.logo_url ? (
                  <button
                    type="button"
                    onClick={handleRemoveLogo}
                    className="text-red-600 hover:text-red-700"
                  >
                    Remove
                  </button>
                ) : null}
              </div>
            </div>

            <div>
              <h2 className="text-lg font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-200">
                Typography
              </h2>
              <p className="text-sm text-gray-500 mb-6">Upload custom fonts (TTF or OTF) for headings and body text. If you do not upload a font, the site default (URW Geometric) is used.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="border border-gray-200 rounded-lg p-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Header Font</label>
                  <p className="text-xs text-gray-500 mb-4">Used for headings (h1, h2, h3, etc.)</p>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Font Name</label>
                      <input
                        type="text"
                        value={formData.header_font_name}
                        onChange={(e) => handleThemeUpdate('header_font_name', e.target.value)}
                        placeholder="e.g. Custom font name or leave blank for site default (URW Geometric)"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Font File</label>
                      {formData.header_font_file_url ? (
                        <div className="flex items-center gap-4 mb-2">
                          <span className="text-sm text-gray-600">
                            {formData.header_font_file_url.split('/').pop()}
                          </span>
                          <button
                            type="button"
                            onClick={() => handleRemoveFont('header')}
                            className="text-red-600 hover:text-red-700 text-sm"
                          >
                            Remove
                          </button>
                        </div>
                      ) : null}
                      <label className="cursor-pointer inline-block px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm font-medium">
                        <span>{uploadingHeaderFont ? 'Uploading...' : 'Upload Font (TTF/OTF)'}</span>
                        <input
                          type="file"
                          className="sr-only"
                          accept=".ttf,.otf"
                          onChange={handleHeaderFontUpload}
                          disabled={uploadingHeaderFont}
                        />
                      </label>
                    </div>
                  </div>
                  {(formData.header_font_name || formData.header_font_file_url || formData.header_font) ? (
                    <p className="mt-3 text-2xl font-bold text-gray-700" style={{ fontFamily: formData.header_font_name || formData.header_font }}>
                      The quick brown fox jumps over the lazy dog.
                    </p>
                  ) : null}
                </div>
                <div className="border border-gray-200 rounded-lg p-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Paragraph Font</label>
                  <p className="text-xs text-gray-500 mb-4">Used for paragraphs and body text</p>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Font Name</label>
                      <input
                        type="text"
                        value={formData.paragraph_font_name}
                        onChange={(e) => handleThemeUpdate('paragraph_font_name', e.target.value)}
                        placeholder="e.g. Custom font name or leave blank for site default (URW Geometric)"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Font File</label>
                      {formData.paragraph_font_file_url ? (
                        <div className="flex items-center gap-4 mb-2">
                          <span className="text-sm text-gray-600">
                            {formData.paragraph_font_file_url.split('/').pop()}
                          </span>
                          <button
                            type="button"
                            onClick={() => handleRemoveFont('paragraph')}
                            className="text-red-600 hover:text-red-700 text-sm"
                          >
                            Remove
                          </button>
                        </div>
                      ) : null}
                      <label className="cursor-pointer inline-block px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm font-medium">
                        <span>{uploadingParagraphFont ? 'Uploading...' : 'Upload Font (TTF/OTF)'}</span>
                        <input
                          type="file"
                          className="sr-only"
                          accept=".ttf,.otf"
                          onChange={handleParagraphFontUpload}
                          disabled={uploadingParagraphFont}
                        />
                      </label>
                    </div>
                  </div>
                  {(formData.paragraph_font_name || formData.paragraph_font_file_url || formData.paragraph_font) ? (
                    <p className="mt-3 text-sm text-gray-600 leading-relaxed" style={{ fontFamily: formData.paragraph_font_name || formData.paragraph_font }}>
                      Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt.
                    </p>
                  ) : null}
                </div>
              </div>
            </div>

            <div className="flex justify-end pt-2">
              <button
                onClick={handleSave}
                disabled={saving || !isValid()}
                className="px-10 py-3 bg-teal-700 text-white rounded-xl hover:bg-teal-600 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? 'Saving...' : 'Save Template'}
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex space-x-8">
                <button
                  onClick={() => setActiveLayoutTab('landing')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeLayoutTab === 'landing'
                      ? 'border-teal-500 text-teal-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Landing Page
                </button>
                <button
                  onClick={() => setActiveLayoutTab('winner')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeLayoutTab === 'winner'
                      ? 'border-teal-500 text-teal-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Comp close page
                </button>
                <button
                  onClick={() => setActiveLayoutTab('comingSoon')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeLayoutTab === 'comingSoon'
                      ? 'border-teal-500 text-teal-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Coming soon page
                </button>
              </nav>
            </div>

            {activeLayoutTab === 'landing' ? (
              <SectionOverview
                key="template-landing-sections"
                campaignId={id}
                sections={sections}
                pageType="landing_page"
                onAddSection={() => handleAddSection('landing_page')}
                onDeleteSection={(sectionId) => handleDeleteSection(sectionId, 'landing_page')}
                onDuplicateSection={(section) => handleDuplicateSection(section, 'landing_page')}
                onUpdateSection={setSections}
                api={api}
                isWinnerPage={false}
                resourceBase="/admin/templates"
                sectionsPath="sections"
              />
            ) : null}

            {activeLayoutTab === 'winner' ? (
              <SectionOverview
                key="template-winner-sections"
                campaignId={id}
                sections={winnerSections}
                pageType="winner_page"
                onAddSection={() => handleAddSection('winner_page')}
                onDeleteSection={(sectionId) => handleDeleteSection(sectionId, 'winner_page')}
                onDuplicateSection={(section) => handleDuplicateSection(section, 'winner_page')}
                onUpdateSection={setWinnerSections}
                api={api}
                isWinnerPage={false}
                resourceBase="/admin/templates"
                sectionsPath="sections"
              />
            ) : null}

            {activeLayoutTab === 'comingSoon' ? (
              <SectionOverview
                key="template-coming-soon-sections"
                campaignId={id}
                sections={comingSoonSections}
                pageType="coming_soon_page"
                onAddSection={() => handleAddSection('coming_soon_page')}
                onDeleteSection={(sectionId) => handleDeleteSection(sectionId, 'coming_soon_page')}
                onDuplicateSection={(section) => handleDuplicateSection(section, 'coming_soon_page')}
                onUpdateSection={setComingSoonSections}
                api={api}
                isWinnerPage={false}
                resourceBase="/admin/templates"
                sectionsPath="sections"
              />
            ) : null}
          </div>
        )}
      </div>

      {showSectionTypeModal ? (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 max-w-2xl w-full mx-4 shadow-2xl">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Add New Section</h3>
            <p className="text-gray-600 mb-8">Choose a section type to add to this template page</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              {[
                { key: 'banner', title: 'Hero Banner', description: 'Add a header/banner section', icon: Images },
                { key: 'content', title: 'Content Block', description: 'Add text, media, and rich content', icon: FileText },
                { key: 'forms', title: 'Form Section', description: 'Add a form placeholder section', icon: ClipboardList },
                { key: 'faqs', title: 'FAQs', description: 'Add frequently asked questions', icon: CircleQuestionMark },
                { key: 'gallery', title: 'Gallery', description: 'Add an image gallery section', icon: Images }
              ].map(({ icon: ItemIcon, ...item }) => (
                <div
                  key={item.key}
                  className={`border-2 rounded-xl p-6 cursor-pointer transition-all ${
                    selectedSectionType === item.key
                      ? 'border-teal-500 bg-teal-50'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                  onClick={() => setSelectedSectionType(item.key)}
                >
                  <div className="flex items-start space-x-3">
                    <input
                      type="radio"
                      name="sectionType"
                      value={item.key}
                      checked={selectedSectionType === item.key}
                      onChange={() => {}}
                      className="mt-1 h-4 w-4 text-teal-600 border-gray-300 focus:ring-teal-500"
                    />
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900 flex items-center">
                        <ItemIcon className="w-4 h-4 mr-2" />
                        {item.title}
                      </h4>
                      <p className="text-sm text-gray-600 mt-1">{item.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowSectionTypeModal(false)}
                className="px-6 py-3 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmAddSection}
                className="px-6 py-3 bg-teal-700 text-white rounded-xl hover:bg-teal-600 transition-colors"
              >
                Add Section
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </AdminLayout>
  );
}

export default TemplateEdit;
